/**
 * 手机端：antd-mobile DatePicker（底部滚轮选年/月/日）
 * —— 输出与 DatePickerF `dateStrValue` 一致：`YYYY-MM-DD` 字符串。
 *
 * 【为何不用 CalendarPicker / CalendarPickerView 月历】
 * 月历格子依赖同一套 Popup + 长滚动区；在微信 web-view 里常见「能弹出但点不中」，
 * 与 pointer-events/react-spring、touch-action、与 Drawer 叠层等多因素相关。
 * 滚轮Picker走 PickerView 列手势，不要求点按「格」，在小程序内嵌里更稳定。
 *
 * @see https://mobile.ant.design/zh/components/picker/picker
 * @see https://mobile.ant.design/zh/components/date-picker
 * 调试：控制台 `window.__FORM_DEBUG_DISPATCH_MOBILE__ = true`
 */
import React, { useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { DatePicker } from 'antd-mobile'
import { CalendarOutlined, DownOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import './MobileBottomSheetDateField.less'

const logDispatchMobileDate =
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV === 'development' || window.__FORM_DEBUG_DISPATCH_MOBILE__)
    ? (...args) => {
        console.log('[MobileBottomSheetDateField]', ...args)
      }
    : undefined

const CALENDAR_MONTH_BACK = 2
const CALENDAR_MONTH_FORWARD = 4

function calendarMinMaxFromAnchor (anchorDayjs) {
  const c = anchorDayjs?.isValid() ? anchorDayjs : dayjs()
  const today = dayjs().startOf('day')

  const anchorMin = c.subtract(CALENDAR_MONTH_BACK, 'month').startOf('month')
  const anchorMax = c.add(CALENDAR_MONTH_FORWARD, 'month').endOf('month')

  const todayMin = today.subtract(CALENDAR_MONTH_BACK, 'month').startOf('month')
  const todayMax = today.add(CALENDAR_MONTH_FORWARD, 'month').endOf('month')

  const min = anchorMin.isBefore(todayMin) ? anchorMin : todayMin
  const max = anchorMax.isAfter(todayMax) ? anchorMax : todayMax

  return {
    min: min.toDate(),
    max: max.toDate()
  }
}

/**「不可早于今日」等规则：下限取锚点区间与今日的较大者（按自然日） */
function clampMinFloorToday (calendarMinDate) {
  const t0 = dayjs().startOf('day').valueOf()
  const cm = calendarMinDate.getTime()
  return new Date(Math.max(cm, t0))
}

/** 将刚渲染的Picker弹层抬高到 Drawer 之上（DatePicker→Picker 未必透传 popupStyle） */
function bumpEmbeddedPickerPopupZ () {
  requestAnimationFrame(() => {
    const list = document.querySelectorAll('.adm-popup.adm-picker-popup')
    const el = list[list.length - 1]
    if (!el?.style) return
    el.style.zIndex = '11050'
    el.style.touchAction = 'manipulation'
  })
}

export default function MobileBottomSheetDateField ({
  value,
  onChange,
  disabledDate,
  placeholder = '请选择日期'
}) {
  const [visible, setVisible] = useState(false)

  const d = useMemo(() => {
    if (value == null || value === '') return null
    const x = dayjs.isDayjs(value) ? value : dayjs(value)
    return x.isValid() ? x : null
  }, [value])

  const display = d ? d.format('YYYY-MM-DD') : null

  const shouldDisableDate = useCallback((date) => {
    if (disabledDate) return disabledDate(dayjs(date))
    return false
  }, [disabledDate])

  const { min: calendarMin, max: calendarMax } = useMemo(
    () => calendarMinMaxFromAnchor(d ?? dayjs()),
    [d]
  )

  const { minDate, maxDate } = useMemo(() => {
    const rawMax = calendarMax
    let rawMin = clampMinFloorToday(calendarMin)
    if (rawMin.getTime() > rawMax.getTime()) {
      rawMin = new Date(rawMax.getTime())
    }
    return { minDate: rawMin, maxDate: rawMax }
  }, [calendarMin, calendarMax])

  const pickerValue = d ? boundDateToRange(d.toDate(), minDate, maxDate) : undefined

  const handleConfirm = useCallback(
    (picked) => {
      logDispatchMobileDate?.('DatePicker onConfirm', picked)
      if (picked == null) return
      const dj = dayjs(picked)
      if (!dj.isValid()) return
      const raw = dj.toDate()
      if (shouldDisableDate(raw)) {
        logDispatchMobileDate?.('rejected by shouldDisableDate', dj.format('YYYY-MM-DD'))
        return
      }
      onChange?.(dj.format('YYYY-MM-DD'))
      setVisible(false)
    },
    [onChange, shouldDisableDate]
  )

  const handleAfterShow = useCallback(() => {
    bumpEmbeddedPickerPopupZ()
    logDispatchMobileDate?.('Picker afterShow')
  }, [])

  return (
    <>
      <button
        type="button"
        className="mobile-bottom-sheet-date-field__trigger"
        onClick={() => {
          logDispatchMobileDate?.('open date picker wheel')
          setVisible(true)
        }}
        aria-haspopup="dialog"
        aria-expanded={visible}
      >
        <span className="mobile-bottom-sheet-date-field__text">
          {display ?? <span className="mobile-bottom-sheet-date-field__ph">{placeholder}</span>}
        </span>
        <span className="mobile-bottom-sheet-date-field__ic" aria-hidden>
          <CalendarOutlined />
          <DownOutlined className="mobile-bottom-sheet-date-field__down" />
        </span>
      </button>
      <DatePicker
        visible={visible}
        onClose={() => setVisible(false)}
        getContainer={() => document.body}
        precision="day"
        min={minDate}
        max={maxDate}
        value={pickerValue}
        title="选择日期"
        cancelText="取消"
        confirmText="确定"
        closeOnMaskClick
        afterShow={handleAfterShow}
        onConfirm={handleConfirm}
        mouseWheel
      />
    </>
  )
}

/** 落在 [min,max] 区间内，便于受控 DatePicker（与上游 antd bound 语义一致） */
function boundDateToRange (raw, minD, maxD) {
  const t = raw.getTime()
  const a = Math.max(minD.getTime(), Math.min(maxD.getTime(), t))
  return new Date(a)
}

MobileBottomSheetDateField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  onChange: PropTypes.func,
  disabledDate: PropTypes.func,
  placeholder: PropTypes.string
}
